import { http, getPaginationUrlQuery } from '~/src/services/requests';
import { PaginatedParams } from '~/src/globalTypes';

export interface TemplateWithValues {
  templateId: number;
  templateDefaultValuesId: number | null;
}
export interface TemplateSetPayload {
  title?: string;
  templateIds?: number[];
  templatesWithValues?: TemplateWithValues[];
}

interface TemplateWithValuesResponse {
  template_id: number;
  template_default_values_id: number | null;
}

export interface OutdatedTemplate {
  org_template_id: number;
  current_pdf_doc_id: number;
  current_form_number: string | null;
  current_form_name: string | null;
  current_global_template_id: number;
  new_pdf_doc_id: number;
  new_form_number: string | null;
  new_form_name: string | null;
  new_global_template_id: number;
  default_values_id: number | null;
}

export interface VersionCheckResponse {
  outdated_templates: OutdatedTemplate[];
}

export interface UpgradeTemplateResponse {
  updated: boolean;
  new_template: {
    id: number;
    title: string | null;
    subtitle: string | null;
  };
  old_template_id: number;
}

export interface TemplateSetApiResponse {
  id: number;
  title: string;
  created_at: string;
  last_modified: string;
  num_templates: number;
  organization_id: number;
  search_text: string;
  templates: number[];
  templates_with_values: TemplateWithValuesResponse[];
}

function templateSetsService() {
  return {
    fetchById: async (
      orgFprint: string,
      templateSetId: number,
    ): Promise<TemplateSetApiResponse> => {
      const parse = true;
      return http.innerSendAuthenticated(
        {
          relativePath: `/clients/api/v2/org/${orgFprint}/template-set/${templateSetId}/`,
          method: 'GET',
        },
        parse,
      );
    },
    update: async (
      orgFprint: string,
      id: number,
      payload: TemplateSetPayload,
    ): Promise<TemplateSetApiResponse> => {
      const parse = true;
      return http.innerSendAuthenticated(
        {
          relativePath: `/clients/api/v2/org/${orgFprint}/template-set/${id}/`,
          method: 'PATCH',
          body: {
            title: payload.title,
            templatesWithValues: payload.templatesWithValues,
          },
        },
        parse,
      );
    },
    delete: async (orgFprint: string, id: number) => {
      const parse = false;
      return http.innerSendAuthenticated(
        {
          relativePath: `/clients/api/v2/org/${orgFprint}/template-set/${id}/`,
          method: 'DELETE',
        },
        parse,
      );
    },
    create: async (
      orgFprint: string,
      payload: TemplateSetPayload,
    ): Promise<TemplateSetApiResponse> => {
      const parse = true;
      return http.innerSendAuthenticated(
        {
          relativePath: `/clients/api/v2/org/${orgFprint}/template-sets/`,
          method: 'POST',
          body: {
            title: payload.title,
            templatesWithValues: payload.templatesWithValues,
          },
        },
        parse,
      );
    },
    fetchAll: async (orgFprint: string): Promise<TemplateSetApiResponse[]> => {
      const parse = true;
      return http.innerSendAuthenticated(
        {
          relativePath: `/clients/api/v1/org/${orgFprint}/template-sets/`,
          method: 'GET',
        },
        parse,
      );
    },
    fetch: async (
      orgFprint: string,
      { page, search, sortBy, order, limit }: PaginatedParams,
    ): Promise<TemplateSetApiResponse[]> => {
      const parse = true;
      return http.innerSendAuthenticated(
        {
          relativePath: `/clients/api/v2/org/${orgFprint}/template-sets/${getPaginationUrlQuery(
            { page, search, sortBy, order, limit },
          )}`,
          method: 'GET',
        },
        parse,
      );
    },
    checkVersions: async (
      orgFprint: string,
      templateSetId: number,
    ): Promise<VersionCheckResponse> => {
      return http.innerSendAuthenticated(
        {
          relativePath: `/clients/api/v2/org/${orgFprint}/template-set/${templateSetId}/version-check/`,
          method: 'GET',
        },
        true,
      );
    },
    upgradeTemplate: async (
      orgFprint: string,
      templateSetId: number,
      oldTemplateId: number,
    ): Promise<UpgradeTemplateResponse> => {
      return http.innerSendAuthenticated(
        {
          relativePath: `/clients/api/v2/org/${orgFprint}/template-set/${templateSetId}/upgrade-template/`,
          method: 'POST',
          body: { old_template_id: oldTemplateId },
        },
        true,
      );
    },
  };
}

export default templateSetsService();
